<template>
  <div id="round-finder-table" class="w-full space-y-4 text-black">
    <Filters
      :options="effectiveOptions"
      :showActionButton="true"
      :search="search"
      :resetFilter="resetFilter"
      :resetPagination="resetPagination"
    />

    <div
      v-if="isLoading"
      class="flex-grow flex flex-col items-center justify-center p-20"
    >
      <Loader />
    </div>

    <div
      v-else
      class="table-container-custom shadow-sm rounded-2xl overflow-hidden border border-1 mb-3"
    >
      <div
        class="p-3 border-b border-slate-100 flex items-center justify-between overflow-auto"
      >
        <ExportDataAdvance
          :column="exportColumns"
          :resource-name="resourceNameProps || 'spins'"
          :options="filterOptions"
        />
      </div>

      <DataTable
        :value="tableData"
        :lazy="true"
        :paginator="true"
        :rows="selectedLimit"
        :totalRecords="totalElements"
        :loading="isLoading"
        :first="(currentPage - 1) * selectedLimit"
        @page="onPage"
        @sort="onSort"
        :sortField="sortField"
        :sortOrder="sortOrder"
        :rowsPerPageOptions="[15, 25, 50, 100]"
        removableSort
        stripedRows
        scrollable
        scrollHeight="flex"
        responsiveLayout="scroll"
        paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
      >
        <template #empty>
          <div class="text-center py-4 text-muted">No rounds found.</div>
        </template>
        <Column
          v-for="col in activeColumns"
          :key="col.slug"
          :field="col.slug"
          :header="col.label || col.slug"
          :sortable="col.sortable !== false && col.type !== 'action_button'"
          headerClass="whitespace-nowrap text-xs uppercase font-bold text-slate-600 px-3 py-3"
          bodyClass="whitespace-nowrap px-3 py-3"
          :class="col.isClickable ? 'cursor-pointer' : ''"
        >
          <!-- Slot for custom parent overrides -->
          <template #body="slotProps">
            <slot
              :name="'col-' + col.slug"
              :data="slotProps.data"
              :value="slotProps.data[col.slug]"
            >
              <!-- Player ID link -->
              <span
                v-if="col.slug === 'user_id' || col.type === 'player_link'"
                @click="openPlayer(slotProps.data)"
                class="text-blue-600 font-bold hover:underline decoration-2 underline-offset-4 cursor-pointer"
              >
                {{ slotProps.data[col.slug] || "-" }}
              </span>

              <!-- Roulette Chip representation -->
              <div
                v-else-if="
                  (col.type === 'chip' || col.type === 'roulette_number') &&
                  slotProps.data[col.slug] !== undefined &&
                  slotProps.data[col.slug] !== null &&
                  slotProps.data[col.slug] !== '' &&
                  Number(slotProps.data[col.slug]) !== 999
                "
                class="flex items-center gap-2"
              >
                <span
                  :class="[
                    'roulette-chip chip-table',
                    getChipColorClass(slotProps.data[col.slug]),
                  ]"
                >
                  <span class="chip-number">{{
                    slotProps.data[col.slug]
                  }}</span>
                </span>
                <span class="text-xs font-semibold text-slate-500">
                  {{ getChipDescriptor(slotProps.data[col.slug]) }}
                </span>
              </div>
              <span
                v-else-if="col.type === 'chip' || col.type === 'roulette_number'"
                class="text-xs font-medium text-slate-400"
              >
                -
              </span>

              <!-- Multiplier badge -->
              <span
                v-else-if="col.type === 'multiplier' || col.isMultiplierField"
                class="px-2.5 py-1 bg-amber-50 text-amber-700 rounded-lg font-black text-xs border border-amber-200/80 shadow-xs inline-flex items-center gap-1"
              >
                <i class="fa-solid fa-bolt text-amber-500 text-[10px]"></i>
                x{{ slotProps.data[col.slug] }}
              </span>

              <!-- Status / Boolean Badge -->
              <span
                v-else-if="col.type === 'badge' || col.type === 'boolean'"
                :class="getBadgeClass(slotProps.data[col.slug], col)"
                class="px-2.5 py-0.5 rounded-full text-[11px] font-bold tracking-wide inline-flex items-center gap-1.5"
              >
                <span
                  class="w-1.5 h-1.5 rounded-full"
                  :class="getBadgeDotClass(slotProps.data[col.slug], col)"
                ></span>
                {{ formatBadgeValue(slotProps.data[col.slug], col) }}
              </span>

              <!-- Code / ID pill -->
              <span
                v-else-if="col.type === 'code' || col.type === 'id'"
                class="text-xs font-mono font-medium text-slate-700 bg-slate-100 px-2 py-0.5 rounded-md border border-slate-200/70 max-w-[220px] truncate inline-block align-middle"
                :title="slotProps.data[col.slug]"
              >
                {{ slotProps.data[col.slug] || "-" }}
              </span>

              <!-- Date format -->
              <span
                v-else-if="col.type === 'date'"
                class="text-xs font-mono text-gray-500"
              >
                {{ formatDate(slotProps.data[col.slug]) }}
              </span>

              <!-- Action button -->
              <template v-else-if="col.type === 'action_button'">
                <button
                  @click="openRoundDetail(slotProps.data)"
                  class="p-2 bg-slate-100 hover:bg-blue-600 hover:text-white text-slate-600 rounded-lg transition-all cursor-pointer"
                  title="View Details"
                >
                  <i class="fa-solid fa-eye"></i>
                </button>
              </template>

              <!-- Default fallback -->
              <span v-else class="text-xs font-medium text-slate-700">
                {{
                  slotProps.data[col.slug] !== undefined &&
                  slotProps.data[col.slug] !== null
                    ? slotProps.data[col.slug]
                    : "-"
                }}
              </span>
            </slot>
          </template>
        </Column>
      </DataTable>
    </div>
  </div>

  <Teleport to="body">
    <component
      :is="modalComponent || 'RoundModal'"
      v-if="showDetailModal"
      :title="modalTitle"
      :closeModal="closeModal"
      :isVisible="showDetailModal"
      :roundInfo="roundInfo"
      :fullInfo="fullInfo"
      :skinFilt="skinFilt"
    />
  </Teleport>
</template>

<script>
import DataTable from "primevue/datatable";
import Column from "primevue/column";
import Filters from "../table/Filters.vue";
import RoundModal from "../Modal/RoundModal.vue";
import ExportDataAdvance from "../table/ExportDataAdvance.vue";
import { getFetch } from "@/api/axiosClient";
import { formatString } from "@/helpers/date.js";
import { mapGetters } from "vuex";
import Loader from "../Loader.vue";

const RED_NUMBERS = new Set([
  1, 3, 5, 7, 9, 12, 14, 16, 18, 19, 21, 23, 25, 27, 30, 32, 34, 36,
]);
const BLACK_NUMBERS = new Set([
  2, 4, 6, 8, 10, 11, 13, 15, 17, 20, 22, 24, 26, 28, 29, 31, 33, 35,
]);

export default {
  name: "RoundFinder",
  components: {
    DataTable,
    Column,
    Filters,
    RoundModal,
    ExportDataAdvance,
    Loader,
  },
  props: {
    category: { type: String, default: null },
    customColumns: { type: Array, default: null },
    customFilters: { type: Array, default: null },
    endpointProps: { type: String, default: "" },
    resourceNameProps: { type: String, default: "spins" },
    defaultSortField: { type: String, default: "updated_at" },
    defaultSortOrder: { type: Number, default: -1 },
    fieldToExclude: { type: Array, default: () => [] },
    skinIdProps: { type: [Number, String], default: null },
    playerIdProps: { type: String, default: "" },
    campaignIdProps: { type: String, default: "" },
    modalComponent: { type: [Object, Function, String], default: null },
  },
  data() {
    return {
      currentPage: 1,
      totalElements: 0,
      tableData: [],
      isLoading: false,
      showDetailModal: false,
      showTooltip: false,
      fullInfo: false,
      roundInfo: [],
      skinFilt: "",

      sortby: "First New",
      sortField: this.defaultSortField || "updated_at",
      sortOrder:
        this.defaultSortOrder !== undefined ? this.defaultSortOrder : -1,
      selectedLimit: 15,

      round_id: "",
      player_id: this.playerIdProps || "",
      bet_id: "",
      game_id: "",
      freebet_id: "",
      campaign_id: "",
      multiplier: "",
      start_date: null,
      end_date: null,

      filterOptions: {},

      options: [
        {
          label: "Round ID",
          type: "text",
          placeholder: "Search for Round ID",
          property: "round_id",
        },
        {
          label: "Player ID",
          type: "text",
          placeholder: "Search for Player ID",
          property: "player_id",
        },
        {
          label: "Bet ID",
          type: "text",
          placeholder: "Search for Bet ID",
          property: "bet_id",
        },
        {
          label: "Campaign Id",
          type: "text",
          placeholder: "Search for Campaign Id",
          property: "campaign_id",
          disabled: true,
        },
        {
          label: "Freebet Id",
          type: "text",
          placeholder: "Search for Freebet Id",
          property: "freebet_id",
        },
      ],

      base_columns: [
        { slug: "roundId", type: "text", label: "Round ID" },
        { slug: "created_at", label: "Start Time", type: "date" },
        { slug: "updated_at", label: "End Time", type: "date" },
      ],
      player_column: [
        {
          slug: "user_id",
          type: "text",
          label: "Player ID",
          isClickable: true,
        },
      ],
      advanced_column: [
        { slug: "uuid", label: "Bet ID", type: "text" },
        { slug: "amount", type: "number", label: "Amount" },
        { slug: "win", type: "number", label: "Win" },
      ],
      action_column: [
        { type: "action_button", label: "Action", slug: "actions" },
      ],

      sortOptions: {
        options: ["First New", "First Old", "Max Multiplier", "Min Multiplier"],
      },
      modalTitle: "",
    };
  },
  watch: {
    playerIdProps(newVal) {
      this.player_id = newVal || "";
      this.updateFilterOptions();
      this.search(false);
    },
    round_id: "updateFilterOptions",
    player_id: "updateFilterOptions",
    bet_id: "updateFilterOptions",
    game_id: "updateFilterOptions",
    freebet_id: "updateFilterOptions",
    campaign_id: "updateFilterOptions",
    multiplier: "updateFilterOptions",
    start_date: "updateFilterOptions",
    end_date: "updateFilterOptions",
  },
  computed: {
    ...mapGetters(["multipleMultipliersGames", "isMenuOpen", "skinStorage"]),

    effectiveOptions() {
      const base =
        this.customFilters && this.customFilters.length > 0
          ? this.customFilters
          : this.options;
      if (this.playerIdProps) {
        return base.map((opt) => {
          if (opt.property === "player_id") {
            return {
              ...opt,
              disabled: true,
              default: this.playerIdProps,
            };
          }
          return opt;
        });
      }
      return base;
    },

    allFieldsEmpty() {
      return (
        !this.round_id?.trim() &&
        !this.player_id?.trim() &&
        !this.bet_id?.trim() &&
        !this.game_id?.trim() &&
        !this.freebet_id?.trim() &&
        !this.campaign_id?.trim() &&
        !this.multiplier?.toString().trim()
      );
    },

    activeColumns() {
      if (this.customColumns && this.customColumns.length > 0) {
        return this.customColumns.filter(
          (col) => !this.fieldToExclude.includes(col.slug),
        );
      }
      return [
        ...this.player_column,
        ...this.base_columns,
        ...this.advanced_column,
        ...this.action_column,
      ].filter(
        (col) =>
          col.slug !== "updated_at" && !this.fieldToExclude.includes(col.slug),
      );
    },

    exportColumns() {
      if (this.customColumns && this.customColumns.length > 0) {
        return this.customColumns.filter(
          (col) =>
            col.type !== "action_button" &&
            !this.fieldToExclude.includes(col.slug),
        );
      }
      return [
        ...this.player_column,
        ...this.base_columns,
        ...this.advanced_column,
      ].filter((col) => !this.fieldToExclude.includes(col.slug));
    },
  },
  async mounted() {
    if (this.campaignIdProps) {
      this.campaign_id = this.campaignIdProps;
    }
    if (this.playerIdProps) {
      this.player_id = this.playerIdProps;
    }

    this.initFilters();
    this.updateFilterOptions();
    await this.checkSkinAndParams();
  },
  methods: {
    getChipColorClass(val) {
      if (val === undefined || val === null || val === "")
        return "chip-color-neutral";
      const num = Number(val);
      if (num === 0) return "chip-color-green";
      if (RED_NUMBERS.has(num)) return "chip-color-red";
      if (BLACK_NUMBERS.has(num)) return "chip-color-black";
      return "chip-color-neutral";
    },

    getChipDescriptor(val) {
      if (val === undefined || val === null || val === "") return "";
      const num = Number(val);
      if (num === 0) return "Zero (Green)";
      const color = RED_NUMBERS.has(num)
        ? "Red"
        : BLACK_NUMBERS.has(num)
        ? "Black"
        : "";
      const parity = num % 2 === 0 ? "Even" : "Odd";
      return `${color} • ${parity}`;
    },

    getBadgeClass(val, col) {
      if (col?.badgeClass && typeof col.badgeClass === "function") {
        return col.badgeClass(val);
      }
      if (
        val === true ||
        val === "true" ||
        val === "active" ||
        val === "ACTIVE"
      ) {
        return "bg-emerald-50 text-emerald-700 border border-emerald-200/80";
      }
      if (
        val === false ||
        val === "false" ||
        val === "closed" ||
        val === "CLOSED" ||
        val === "completed"
      ) {
        return "bg-slate-100 text-slate-600 border border-slate-200";
      }
      return "bg-blue-50 text-blue-700 border border-blue-200";
    },

    getBadgeDotClass(val) {
      if (
        val === true ||
        val === "true" ||
        val === "active" ||
        val === "ACTIVE"
      ) {
        return "bg-emerald-500 animate-pulse";
      }
      return "bg-slate-400";
    },

    formatBadgeValue(val, col) {
      if (col?.badgeLabels && col.badgeLabels[val] !== undefined) {
        return col.badgeLabels[val];
      }
      if (val === true) return "Active";
      if (val === false) return "Completed";
      return val !== undefined && val !== null ? String(val) : "-";
    },

    updateFilterOptions() {
      this.filterOptions = {
        round_id: this.round_id,
        player_id: this.player_id,
        bet_id: this.bet_id,
        game_id: this.game_id,
        freebet_id: this.freebet_id,
        campaign_id: this.campaign_id,
        multiplier: this.multiplier,
        start_date: this.start_date,
        end_date: this.end_date,
      };
    },

    async onSort(event) {
      this.sortField = event.sortField;
      this.sortOrder = event.sortOrder;
      this.currentPage = 1;
      await this.search();
    },

    formatDate(date) {
      return date ? new Date(date).toLocaleString() : "-";
    },

    onPage(event) {
      this.currentPage = event.page + 1;
      this.selectedLimit = event.rows;
      this.search(false);
    },

    initFilters() {
      this.effectiveOptions.forEach((opt) => {
        if (opt.default) this[opt.property] = opt.default;
      });
      if (this.playerIdProps) {
        this.player_id = this.playerIdProps;
      }
    },

    async checkSkinAndParams() {
      const params = new URLSearchParams(window.location.search);
      this.skinFilt =
        params.get("skin_id") ?? this.skinStorage ?? this.skinIdProps ?? "";

      this.start_date = this.getInitialTime("start");
      this.end_date = this.getInitialTime("end");

      const query = this.$route?.query ?? {};
      if (this.playerIdProps) {
        this.player_id = this.playerIdProps;
      } else if (query.user_id) {
        this.player_id = query.user_id;
      }
      if (query.round_id) this.round_id = query.round_id;
      if (query.game_id) this.game_id = query.game_id;

      if (
        this.skinFilt &&
        (this.playerIdProps || query.user_id || query.round_id || query.game_id || this.campaign_id)
      ) {
        this.search(false);
      }
    },

    getInitialTime(type) {
      return formatString(null, {
        hours: type === "start" ? 0 : 23,
        minutes: type === "start" ? 0 : 59,
      });
    },

    async search(cleanUrl = true) {
      if (cleanUrl) this.$router.replace({ query: {} });

      this.isLoading = true;
      let endpoint = "";
      if (this.endpointProps) {
        endpoint = this.endpointProps;
        this.fullInfo = true;
      } else {
        endpoint = this.allFieldsEmpty ? "multipliers/" : "spins/";
        this.fullInfo = !this.allFieldsEmpty;
      }

      const params = {
        skin_id: this.skinFilt,
        page: this.currentPage,
        limit: this.selectedLimit,
        sortField: this.sortField,
        sortOrder: this.sortOrder === 1 ? "asc" : "desc",
        start_date: this.start_date ? new Date(this.start_date + "Z") : null,
        end_date: this.end_date ? new Date(this.end_date + "Z") : null,
      };

      if (this.endpointProps) {
        if (this.round_id) params.roundId = this.round_id.trim();
        if (this.player_id) params.user_id = this.player_id.trim();
        if (this.bet_id) params.uuid = this.bet_id.trim();
        if (this.game_id) {
          params.game_id = this.game_id.trim();
          params.gameId = this.game_id.trim();
        }
        if (this.freebet_id) params.freebet_id = this.freebet_id.trim();
        if (this.campaign_id) params.campaign_id = this.campaign_id.trim();
        if (this.multiplier) params.multiplier = this.multiplier;
      } else if (!this.allFieldsEmpty) {
        if (this.round_id) params.roundId = this.round_id.trim();
        if (this.player_id) params.user_id = this.player_id.trim();
        if (this.bet_id) params.uuid = this.bet_id.trim();
        if (this.game_id) {
          params.game_id = this.game_id.trim();
          params.gameId = this.game_id.trim();
        }
        if (this.freebet_id) params.freebet_id = this.freebet_id.trim();
        if (this.campaign_id) params.campaign_id = this.campaign_id.trim();
      }

      try {
        const res = await getFetch(endpoint, params);
        this.tableData = res?.data || [];
        this.totalElements = res?.totalItems || 0;
      } catch (e) {
        console.error(e);
      } finally {
        this.isLoading = false;
      }
    },

    async sortAndReset() {
      if (this.sortby === "First New") {
        this.sortField = "updated_at";
        this.sortOrder = -1;
      } else if (this.sortby === "First Old") {
        this.sortField = "updated_at";
        this.sortOrder = 1;
      } else if (this.sortby === "Max Multiplier") {
        this.sortField = "multiplier";
        this.sortOrder = -1;
      } else if (this.sortby === "Min Multiplier") {
        this.sortField = "multiplier";
        this.sortOrder = 1;
      }

      this.currentPage = 1;
      await this.search();
    },

    async resetFilter() {
      this.currentPage = 1;
      this.sortby = "First New";
      this.sortField = this.defaultSortField || "updated_at";
      this.sortOrder =
        this.defaultSortOrder !== undefined ? this.defaultSortOrder : -1;
      this.player_id = this.playerIdProps || "";
      this.round_id = "";
      this.bet_id = "";
      this.game_id = "";
      this.freebet_id = "";
      this.multiplier = "";
      this.campaign_id = this.campaignIdProps || "";
      this.start_date = this.getInitialTime("start");
      this.end_date = this.getInitialTime("end");
      this.updateFilterOptions();
      await this.search();
    },

    async openPlayer(row) {
      const user_id = row.user_id;
      if (!user_id) return;
      const skin = parseInt(user_id.split("_")[0]);
      if (!skin) return alert("Demo Player");
      const res = await getFetch(`players/detail/${user_id}?skin_id=${skin}`);
      if (res) {
        const lang = this.$route?.params?.lang || "en";
        const currentSkin =
          this.skinFilt ||
          this.skinStorage ||
          this.$route?.params?.skinFilt ||
          skin;
        const skinPath =
          currentSkin && currentSkin !== "all" ? `/${currentSkin}` : "";
        const category =
          this.category ||
          this.$route?.meta?.section ||
          (this.$route?.path?.includes("/livecasino")
            ? "livecasino"
            : this.$route?.path?.includes("/instant")
            ? "instant"
            : "slot");

        window.open(
          `/${lang}${skinPath}/${category}/players/${res._id}`,
          "_blank"
        );
      }
    },

    async openRoundDetail(row) {
      this.modalTitle = "Round Details";
      this.roundInfo = row;
      this.showDetailModal = true;
    },

    openRoundHistory(bet) {
      if (!bet || !bet.roundId || !bet.game_id) return;
      const baseUrl =
        process.env.VUE_APP_HISTORY ||
        "https://stage-casino.theearplatform.com/stagemoregames/historyPage";
      const url = `${baseUrl}/?round_id=${bet.roundId}&game_id=${
        bet.game_id
      }&token=${bet.token || "demo"}&provider_id=207`;
      window.open(url, "_blank");
    },

    closeModal() {
      this.showDetailModal = false;
    },
  },
};
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

/* =========================================
   3D ROULETTE CHIPS IN TABLE
========================================= */
.roulette-chip {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  font-family: inherit;
  font-weight: 800;
  font-variant-numeric: tabular-nums;
  user-select: none;
  flex-shrink: 0;
  box-sizing: border-box;
}

.chip-number {
  position: relative;
  z-index: 2;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.7);
}

.chip-table {
  width: 28px;
  height: 28px;
  font-size: 11px;
}

/* RED ROULETTE CHIP */
.chip-color-red {
  background: radial-gradient(
    circle at 35% 30%,
    #ff5252 0%,
    #e11d48 55%,
    #9f1239 100%
  );
  border: 1.5px solid #fecdd3;
  color: #ffffff;
  box-shadow: 0 2px 5px rgba(225, 29, 72, 0.3),
    inset 0 1px 2px rgba(255, 255, 255, 0.45);
}

/* BLACK ROULETTE CHIP */
.chip-color-black {
  background: radial-gradient(
    circle at 35% 30%,
    #475569 0%,
    #1e293b 65%,
    #0f172a 100%
  );
  border: 1.5px solid #94a3b8;
  color: #ffffff;
  box-shadow: 0 2px 5px rgba(15, 23, 42, 0.35),
    inset 0 1px 2px rgba(255, 255, 255, 0.3);
}

/* ZERO GREEN CHIP */
.chip-color-green {
  background: radial-gradient(
    circle at 35% 30%,
    #34d399 0%,
    #059669 60%,
    #064e3b 100%
  );
  border: 1.5px solid #a7f3d0;
  color: #ffffff;
  box-shadow: 0 2px 5px rgba(5, 150, 105, 0.35),
    inset 0 1px 2px rgba(255, 255, 255, 0.45);
}

.chip-color-neutral {
  background: radial-gradient(
    circle at 35% 30%,
    #818cf8 0%,
    #4f46e5 60%,
    #312e81 100%
  );
  border: 1.5px solid #c7d2fe;
  color: #ffffff;
  box-shadow: 0 2px 5px rgba(79, 70, 229, 0.3);
}
</style>
